import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, TablePagination, Chip, Select, FormControl, InputLabel
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { useSnackbar } from '../Components/Snackbar/SnackbarContext';
import * as XLSX from 'xlsx';

import apiEndpoints from '../apiconfig';

const ViewData = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(["Full Name", "Contact", "Mail ID", "Address", "Status", "Remark"]);
  const statusOptions = ["In Discussion", "Interested", "Not Interested", "Follow-up", "Enrolled", "Closed"];

  const { showSnackbar } = useSnackbar();

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(apiEndpoints.sheet_data);
      const result = await response.json();
      if (result.status === 'success') {
        const fetchedData = result.data || [];
        setData(fetchedData);
      }
    } catch (e) {
      console.error("Failed to fetch sheet data", e);
      showSnackbar('Error loading data from server', 'error');
    }
  };

  // --- Handlers for Pagination ---
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- Handlers for Dropdown Menu ---
  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // --- Handlers for Edit ---
  const handleEditClick = () => {
    setEditFormData(data[selectedRowIndex]);
    setEditModalOpen(true);
    setAnchorEl(null);
  };

  const handleEditChange = (col, value) => {
    setEditFormData(prev => ({ ...prev, [col]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(apiEndpoints.sheet_data, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          ...editFormData
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showSnackbar('Record updated successfully', 'success');
        fetchData();
      } else {
        showSnackbar(result.message || 'Error updating record', 'error');
      }
    } catch (e) {
      showSnackbar('Connection error', 'error');
    }
    setEditModalOpen(false);
    setSelectedRowIndex(null);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setSelectedRowIndex(null);
  };

  // --- Handlers for Delete ---
  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    const selectedRecord = data[selectedRowIndex];
    try {
      const response = await fetch(apiEndpoints.sheet_data, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          id: selectedRecord.id
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showSnackbar('Record deleted successfully', 'success');
        fetchData();
      } else {
        showSnackbar(result.message || 'Error deleting record', 'error');
      }
    } catch (e) {
      showSnackbar('Connection error', 'error');
    }
    setDeleteModalOpen(false);
    setSelectedRowIndex(null);
  };

  const handleCloseDelete = () => {
    setDeleteModalOpen(false);
    setSelectedRowIndex(null);
  };

  const handleUpdateField = async (id, field, value) => {
    // Find the full row data from the current state to ensure we send all fields back
    const currentRow = data.find(row => row.id === id);
    if (!currentRow) {
      console.error("Row not found", id);
      return;
    }

    const updatedRow = { ...currentRow, [field]: value };

    try {
      const response = await fetch(apiEndpoints.sheet_data, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          ...updatedRow
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showSnackbar(`Updated ${field} successfully`, 'success');
        // Update local state to keep UI in sync
        setData(prevData => prevData.map(row => row.id === id ? updatedRow : row));
      } else {
        showSnackbar(result.message || `Error updating ${field}`, 'error');
      }
    } catch (e) {
      showSnackbar('Connection error', 'error');
    }
  };





  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B', mb: 1 }}>
            Sheet Data Viewer
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B' }}>
            Viewing records cleanly merged and deduped from uploaded sheets.
          </Typography>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          bgcolor: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          overflow: 'auto',
          maxHeight: '70vh'
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: '#334155',
                  bgcolor: 'rgba(241, 245, 249, 0.95)',
                  backdropFilter: 'blur(4px)',
                  width: 80
                }}
              >
                S.No
              </TableCell>
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 600,
                    color: '#334155',
                    bgcolor: 'rgba(241, 245, 249, 0.95)',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  {col}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: '#334155',
                  bgcolor: 'rgba(241, 245, 249, 0.95)',
                  backdropFilter: 'blur(4px)',
                  width: 140,
                  pr: 4
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748B' }}>
                  No sheet data available. Upload files to view them here.
                </TableCell>
              </TableRow>
            ) : (
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, relativeIndex) => {
                const globalIndex = page * rowsPerPage + relativeIndex;
                return (
                  <TableRow
                    key={globalIndex}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { bgcolor: 'rgba(248, 250, 252, 0.8)' },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell sx={{ color: '#64748B', fontWeight: 500 }}>
                      {globalIndex + 1}
                    </TableCell>
                    {columns.map((col, colIdx) => (
                      <TableCell key={colIdx} sx={{ color: '#0F172A' }}>
                        {col === 'Status' ? (
                          <Select
                            value={row[col] || ''}
                            onChange={(e) => handleUpdateField(row.id, 'Status', e.target.value)}
                            size="small"
                            sx={{
                              fontSize: '0.8125rem',
                              borderRadius: 2,
                              minWidth: 120,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                              },
                              bgcolor: row[col] === 'Enrolled' ? 'rgba(34, 197, 94, 0.1)' : 
                                       row[col] === 'Not Interested' ? 'rgba(239, 68, 68, 0.1)' : 
                                       'rgba(255, 255, 255, 0.5)'
                            }}
                          >
                            {statusOptions.map((opt) => (
                              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8125rem' }}>{opt}</MenuItem>
                            ))}
                          </Select>
                        ) : col === 'Remark' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              value={row[col] || ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setData(prevData => prevData.map(r => r.id === row.id ? { ...r, Remark: newValue } : r));
                              }}
                              size="small"
                              placeholder="Add remark..."
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                                sx: { 
                                  fontSize: '0.8125rem', 
                                  px: 1, 
                                  py: 0.5, 
                                  borderRadius: 1, 
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                                  minWidth: 150
                                }
                              }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => handleUpdateField(row.id, 'Remark', row[col])}
                              sx={{ 
                                color: '#10B981',
                                bgcolor: 'rgba(16, 185, 129, 0.1)',
                                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' }
                              }}
                            >
                              <CheckIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Box>
                        ) : (
                          typeof row[col] === 'object' ? JSON.stringify(row[col]) : row[col]
                        )}
                      </TableCell>
                    ))}

                    <TableCell align="right" sx={{ pr: 4 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, globalIndex)}
                        sx={{ color: '#64748B' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* 3-Dot Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            minWidth: 120,
            mt: 0.5
          }
        }}
      >
        <MenuItem onClick={handleEditClick} sx={{ color: '#3B82F6', fontSize: '0.875rem', py: 1 }}>
          <EditIcon fontSize="small" sx={{ mr: 1.5 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#EF4444', fontSize: '0.875rem', py: 1 }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEdit}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, minWidth: { xs: 300, sm: 400 } }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1E293B' }}>Edit Record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {columns.map((col, idx) => (
              col === 'Status' ? (
                <FormControl key={idx} fullWidth variant="outlined">
                  <InputLabel>{col}</InputLabel>
                  <Select
                    value={editFormData[col] || ''}
                    label={col}
                    onChange={(e) => handleEditChange(col, e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  key={idx}
                  label={col}
                  fullWidth
                  variant="outlined"
                  multiline={col === 'Remark'}
                  rows={col === 'Remark' ? 3 : 1}
                  value={editFormData[col] || ''}
                  onChange={(e) => handleEditChange(col, e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
              )
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseEdit}
            sx={{ color: '#64748B', fontWeight: 600, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              bgcolor: '#4F46E5',
              '&:hover': { bgcolor: '#4338CA' },
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDelete}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, maxWidth: 400 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1E293B' }}>Delete Record</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569' }}>
            Are you sure you want to delete this record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDelete}
            sx={{ color: '#64748B', fontWeight: 600, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: '#EF4444',
              '&:hover': { bgcolor: '#DC2626' },
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none'
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ViewData;
